import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import './CommonModal.css';

// Function to clean up Bootstrap modal DOM artifacts
const closeAllBootstrapModals = () => {
    // Use requestAnimationFrame and setTimeout to let React finish its unmounting cycle first
    requestAnimationFrame(() => {
        setTimeout(() => {
            try {
                // Remove modal backdrop (this is the main issue - leftover backdrops prevent interaction)
                const backdrops = document.querySelectorAll('.modal-backdrop');
                backdrops.forEach(backdrop => {
                    // Check if backdrop is still in the DOM before removing
                    if (backdrop && backdrop.parentNode) {
                        backdrop.remove();
                    }
                });
                
                // Remove body class that prevents scrolling
                if (document.body) {
                    document.body.classList.remove('modal-open');
                    document.body.style.overflow = '';
                    document.body.style.paddingRight = '';
                }
                
                // Force close any modals that are still showing
                const visibleModals = document.querySelectorAll('.modal.show, .modal[style*="display: block"]');
                visibleModals.forEach(modal => {
                    // Check if modal is still in the DOM before manipulating
                    if (modal && modal.parentNode) {
                        modal.classList.remove('show');
                        // Remove inline display style to allow React Bootstrap to control it
                        if (modal.style && modal.style.display) {
                            modal.style.removeProperty('display');
                        }
                        modal.setAttribute('aria-hidden', 'true');
                        modal.removeAttribute('aria-modal');
                    }
                });
            } catch (error) {
                // Silently handle any DOM manipulation errors during cleanup
                // This can happen if React has already removed the nodes
                console.debug('Modal cleanup error (safe to ignore):', error);
            }
        }, 150);
    });
};

const CommonModal = ({ title, children, footer, modalStatus, classList, changeActiveStatus = () => { }, lightBox = false, closeModalOnEsc = true, showFooter = true, ...rest }) => {
    const [show, setShow] = useState(false);
    
    useEffect(() => {
        setShow(modalStatus);
    }, [modalStatus]);

    const handleClose = useCallback(() => {
        setShow(false);
        // Delay parent notification and cleanup so the exit animation (e.g. hide towards top) runs
        // before the parent unmounts the modal. Same behaviour for close button and Escape.
        setTimeout(() => {
            changeActiveStatus(false);
            closeAllBootstrapModals();
        }, 300);
    }, [changeActiveStatus]);

    // close modal on esc
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (closeModalOnEsc && event.key === 'Escape' && modalStatus) {
                handleClose();
            }

            // Handle Enter key to trigger Yes button
            if (event.key === 'Enter' && modalStatus) {
                const yesButton = document.querySelector('.common-modal-yes-button button');
                if (yesButton) {
                    yesButton.click();
                }
            }
        };
        

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [modalStatus, closeModalOnEsc, handleClose]);

    return (
        <Modal
            scrollable={true}
            show={show}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
            className={`${classList != undefined ? classList : ''} ${lightBox ? 'lightboxModal' : ''}`}
            {...rest}
        >
            {!lightBox &&
                <Modal.Header closeButton className="cursor-grab">
                    <Modal.Title>
                        {title ?? ''}
                    </Modal.Title>
                    <div className='d-flex'>
                        <FontAwesomeIcon onClick={handleClose} icon={faXmark} className='au-modal-close-btn' />
                    </div>
                </Modal.Header>
            }
            <Modal.Body>
                {children}
            </Modal.Body>
            {
                showFooter && <>
                    {!lightBox ?
                        <Modal.Footer>
                            {footer}
                        </Modal.Footer> :
                        <Modal.Footer>
                            <FontAwesomeIcon
                                onClick={handleClose}
                                icon={faXmark}
                                className='au-modal-close-btn'
                            />
                        </Modal.Footer>
                    }
                </>
            }
        </Modal>
    )
}
export default React.memo(CommonModal);